import { Button, Flex, Space } from "antd";
import type { ColumnType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { useIntl } from "react-intl";
import styles from "~components/AddPersonRolesModal/AddPersonRolesModal.module.css";
import { DataTableDateColumn } from "~components/DataTable/components/DataTableDateColumn/DataTableDateColumn.tsx";
import ImageView from "~components/ImageView/ImageView.tsx";
import { dateFormat } from "~constants/dateFormat.ts";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import type { AddPlayerToRosterTableRow } from "~pages/Roster/components/AddPlayerToRoster/types/AddPlayerToRosterTableRow";
import type { RosterShort } from "~types/rosterShort";
import { findImageByTypes } from "~utilities/findImageByTypes.ts";

const AddButton = ({ disabled, onClick }: { disabled: boolean; onClick: () => void }) => {
  return (
    <Button
      type="default"
      size="small"
      icon={<i className="fas fa-plus" />}
      onClick={onClick}
      className={styles.addButton}
      disabled={disabled}
    />
  );
};

export const useGetAddPersonToRosterColumns = (
  isLoading: boolean,
  handleAdd: (row: AddPlayerToRosterTableRow) => void,
  isAddAllowed = true,
  roster: Array<any>,
  fixtureId?: string,
  seasonRosterShort?: RosterShort[],
) => {
  const { formatMessage } = useIntl();

  const columns: ColumnType<AddPlayerToRosterTableRow>[] = [
    {
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameFullLocal",
      sorter: true,
      defaultSortOrder: "ascend" as SortOrder,
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
      render: (value: string, row: AddPlayerToRosterTableRow) => (
        <Space size="small">
          <ImageView imageUrl={row.avatarUrl} size={ImageSizes.Small} width={25} height={25} imageType={ImageTypes.PersonHead} />
          {value}
        </Space>
      ),
    },
    ...(seasonRosterShort
      ? [
          {
            title: formatMessage({ id: "team" }),
            dataIndex: "entityNameFullLocal",
            render: (_: string, row: AddPlayerToRosterTableRow) => {
              const rosterEntry = seasonRosterShort.find((item) => item.personId === row.personId);
              if (!rosterEntry) {
                return "";
              }

              const logoUrl = findImageByTypes(rosterEntry.entityImages ?? [], [ImageTypes.Logo, ImageTypes.LogoAlternate])?.url;

              return (
                <Space size="small">
                  <ImageView imageUrl={logoUrl} size={ImageSizes.Small} width={25} height={25} imageType={ImageTypes.Logo} />
                  {rosterEntry.entityNameFullLocal}
                </Space>
              );
            },
          },
        ]
      : []),
    {
      title: formatMessage({ id: "nationality" }),
      dataIndex: "nationality",
    },
    {
      title: formatMessage({ id: "gender" }),
      dataIndex: "gender",
    },
    {
      title: formatMessage({ id: "date.of.birth" }),
      dataIndex: "dob",
      render: (value: string) => <DataTableDateColumn value={value} format={dateFormat} />,
    },
    ...(fixtureId
      ? [
          {
            title: formatMessage({ id: "roster.number" }),
            dataIndex: "bib",
            width: 20,
          },
          {
            title: formatMessage({ id: "position" }),
            dataIndex: "position",
            width: 20,
          },
          {
            title: formatMessage({ id: "roster.captain" }),
            dataIndex: "isCaptain",
            width: 20,
            render: (value: boolean) => <i className={value ? "far fa-check-square" : "far fa-square"} />,
          },
        ]
      : []),
    {
      title: formatMessage({ id: "status" }),
      dataIndex: "status",
    },
    {
      align: "right",
      render: (_, record: AddPlayerToRosterTableRow) => {
        const existingRosterEntry = roster.find((r) => r.personId === record.personId);
        if (existingRosterEntry) {
          return (
            <Flex justify="center" align="center" style={{ marginLeft: "auto", width: 24 }}>
              <i className="fas fa-check" />
            </Flex>
          );
        }

        const seasonRosterEntry = seasonRosterShort?.find((item) => item.personId === record.personId);
        const isInSeasonRoster = !!seasonRosterEntry;

        return <AddButton disabled={isLoading || !isAddAllowed || isInSeasonRoster} onClick={() => handleAdd(record)} />;
      },
    },
  ];

  return columns;
};
